import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    BarChart3,
    FileSignature,
    Globe,
    Images,
    Loader2,
    Newspaper,
    RefreshCw,
    Sparkles,
} from "lucide-react";
import { crud, siteStatsDisplay } from "../../lib/api";

const CARDS = [
    {
        tab: "slider",
        title: "Anasayfa Slider",
        desc: "Görsel, başlık ve yayın durumu",
        icon: Images,
        resource: "slider",
    },
    {
        tab: "haberler",
        title: "Haberler & Blog",
        desc: "Kamu site haber içerikleri",
        icon: Newspaper,
        resource: "haberler",
    },
    {
        tab: "istatistikler",
        title: "Anasayfa Sayaçları",
        desc: "DB veya özelleştirilmiş istatistik",
        icon: BarChart3,
        resource: null,
    },
    {
        tab: "basvuru-formlari",
        title: "Web Başvuruları",
        desc: "mbshukuk.com form kayıtları",
        icon: FileSignature,
        resource: "basvuru-formlari",
    },
    {
        tab: "pwa",
        title: "PWA Kullanıcıları",
        desc: "Mobil uygulama kayıtları",
        icon: Sparkles,
        resource: "pwa-kullanicilari",
    },
];

export default function SiteYonetimiOzetTab() {
    const [loading, setLoading] = useState(true);
    const [counts, setCounts] = useState({});
    const [statsPreview, setStatsPreview] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const countPairs = await Promise.all(
                CARDS.filter((c) => c.resource)
                    .map(async (c) => {
                        try {
                            const data = await crud.list(c.resource, { page: 1, page_size: 1 });
                            const n = Array.isArray(data)
                                ? data.length
                                : data.count ?? (data.results || []).length;
                            return [c.resource, n];
                        } catch {
                            return [c.resource, null];
                        }
                    }),
            );
            setCounts(Object.fromEntries(countPairs));
            try {
                const d = await siteStatsDisplay.get();
                setStatsPreview(d.display || d.real || {});
            } catch {
                setStatsPreview(null);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div className="p-4 lg:p-6 space-y-6 animate-fade-in">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-white">Kamu Site Komuta Merkezi</h2>
                    <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                        mbshukuk.com içeriği, slider, haberler, istatistikler ve web başvuruları tek
                        merkezden yönetilir. Backend API aynı kalır; sadece panel navigasyonu birleşir.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={load}
                        disabled={loading}
                        className="h-9 px-3 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-sm inline-flex items-center gap-2"
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                        Yenile
                    </button>
                    <a
                        href="https://mbshukuk.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-9 px-3 rounded-lg border border-[#D4AF37]/30 text-[#F3E5AB] hover:bg-[#D4AF37]/10 text-sm inline-flex items-center gap-2"
                    >
                        <Globe className="h-4 w-4" />
                        Canlı site
                    </a>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-16 text-slate-500">
                    <Loader2 className="h-8 w-8 animate-spin" />
                </div>
            ) : (
                <>
                    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                        {CARDS.map((c) => {
                            const Icon = c.icon;
                            const count =
                                c.resource != null ? counts[c.resource] : statsPreview?.muvekkil_sayisi;
                            return (
                                <Link
                                    key={c.tab}
                                    to={`/yeni/site-yonetimi/${c.tab}`}
                                    className="rounded-2xl border border-white/10 bg-[#0a1018]/80 p-5 hover:border-[#D4AF37]/35 transition group"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="h-10 w-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center shrink-0 group-hover:bg-[#D4AF37]/20">
                                            <Icon className="h-5 w-5 text-[#D4AF37]" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-white">{c.title}</div>
                                            <div className="text-xs text-slate-500 mt-0.5">{c.desc}</div>
                                            {count != null ? (
                                                <div className="text-[11px] text-slate-400 mt-2 font-mono">
                                                    Kayıt: {count}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>

                    {statsPreview ? (
                        <div className="rounded-2xl border border-white/10 bg-[#121A2F]/60 p-5">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">
                                Anasayfa sayaç özeti
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                {[
                                    ["muvekkil_sayisi", "Müvekkil"],
                                    ["sigorta_dosya_sayisi", "Sigorta dosyası"],
                                    ["deger_kaybi_sayisi", "Değer kaybı"],
                                    ["deger_kaybi_30_gun", "Son 30 gün DK"],
                                ].map(([key, label]) => (
                                    <div key={key} className="rounded-lg bg-black/20 px-3 py-2 border border-white/5">
                                        <div className="text-[10px] text-slate-500">{label}</div>
                                        <div className="font-mono text-[#F3E5AB] mt-0.5">
                                            {statsPreview[key] ?? "—"}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Link
                                to="/yeni/site-yonetimi/istatistikler"
                                className="inline-block mt-4 text-xs text-[#D4AF37] hover:underline"
                            >
                                İstatistik ayarlarını düzenle →
                            </Link>
                        </div>
                    ) : null}
                </>
            )}
        </div>
    );
}
